/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2025 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import { axiosInstanceRegion } from "../../config/axios-region";
import {
  ContentType,
  expandHtmlPlainTextCompatibleContents,
  getBlobUrlForPDFPreview,
  IDiagnosticReport,
  IDiagnosticReportContent,
  isViewAsTextContent,
} from "@advapacs/common";
import { AppThunk } from "../../config/store";
import { DiagnosticAction } from "../slices/diagnosticReportSlice";
import { orderApi, StudyApi } from "@src/config/api";

export const getOrderDiagnosticReport =
  (portalId: string, patientId: string, orderId: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(DiagnosticAction.getReportStart());
      const response = await axiosInstanceRegion.get(
        orderApi.DIAGNOSTIC_REPORT(portalId, patientId, orderId)
      );
      response.data.contents = expandHtmlPlainTextCompatibleContents(
        response.data.contents
      );
      dispatch(
        DiagnosticAction.getReportSuccess(response.data as IDiagnosticReport)
      );
    } catch (err) {
      dispatch(DiagnosticAction.getReportFail(err?.response?.data));
    }
  };

export const getStudyDiagnosticReport =
  (portalId: string, patientId: string, studyId: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(DiagnosticAction.getReportStart());
      const response = await axiosInstanceRegion.get(
        StudyApi.DIAGNOSTIC_REPORT(portalId, patientId, studyId)
      );
      response.data.contents = expandHtmlPlainTextCompatibleContents(
        response.data.contents
      );
      dispatch(
        DiagnosticAction.getReportSuccess(response.data as IDiagnosticReport)
      );
    } catch (err) {
      dispatch(DiagnosticAction.getReportFail(err?.response?.data));
    }
  };

export const orderFetchReportDataContent = async (
  portalId: string,
  patientId: string,
  studyId: string,
  selectContent: IDiagnosticReportContent,
  report: boolean
): Promise<string> => {
  let result = "";
  if (selectContent.contentType === ContentType.APPLICATION_PDF) {
    const hasParams = !(!report && selectContent?.tag === ContentType.PDF_TAG);
    const path = report
      ? orderApi.ORDER_GENERATE_PDF_REPORT_PDF(
          portalId,
          patientId,
          studyId,
          selectContent.id
        )
      : orderApi.ORDER_GET_DIAGNOSTIC_REPORT_CONTENT(
          portalId,
          patientId,
          studyId,
          selectContent.id
        );
    result = await getBlobUrlForPDFPreview(
      hasParams,
      path,
      axiosInstanceRegion.defaults.baseURL
    );
  } else {
    const viewAsText = isViewAsTextContent(selectContent);
    const response = await axiosInstanceRegion.get(
      orderApi.ORDER_GET_DIAGNOSTIC_REPORT_CONTENT(
        portalId,
        patientId,
        studyId,
        selectContent.id
      ),
      {
        params: viewAsText ? { viewAsText: true } : undefined,
      }
    );
    result = response.data;
  }
  return result;
};
